import { Bell, Camera, Check, Cloud, Database, PlayCircle, RefreshCw, Shield, Trash2, User, Zap } from "lucide-react"; // Added PlayCircle
import React, { useEffect, useState, useCallback } from "react"; // Added useCallback
import { ConfirmDialog } from "../components/ConfirmDialog";
import { VersionDisplay } from "../components/VersionDisplay";
import { useSpotifyAuth } from "../hooks/useSpotifyAuth";
import { useToast } from "@/hooks/useToast";
import { dbHelpers, DB_NAME } from "../lib/db"; // Imported dbHelpers
import { useProfileStore } from "../stores/profileStore";
import { formatFileSize } from "../utils/formatFileSize";

const accentColors = [
  { name: "Green", value: "#22c55e" },
  { name: "Blue", value: "#3b82f6" },
  { name: "Purple", value: "#a855f7" },
  { name: "Pink", value: "#ec4899" },
  { name: "Orange", value: "#f97316" },
];

const SettingsSection = ({ icon, title, children }) => (
  <section className="bg-zinc-800/50 rounded-lg p-6 shadow-lg hover:shadow-xl transition-all duration-300">
    <div className="flex items-center gap-3 mb-6">
      {icon}
      <h2 className="text-2xl font-bold">{title}</h2>
    </div>
    {children}
  </section>
);

const ToggleSwitch = ({ checked, onChange, label, description }) => (
  <div className="flex items-center justify-between">
    <div>
      <h3 className="font-medium">{label}</h3>
      <p className="text-sm text-zinc-400">{description}</p>
    </div>
    <label className="relative inline-flex items-center cursor-pointer">
      <input type="checkbox" checked={checked} onChange={onChange} className="sr-only peer" />
      <div className="w-11 h-6 bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
    </label>
  </div>
);

export const Settings = () => {
  const { profile, updateProfile, resetProfile } = useProfileStore();
  const { isAuthenticated, login, logout } = useSpotifyAuth();
  const { toast } = useToast();
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [appSize, setAppSize] = useState<{ total: number; localStorage: number; indexedDB: number }>({
    total: 0,
    localStorage: 0,
    indexedDB: 0,
  });
  const [isCalculating, setIsCalculating] = useState(false);
  const [email, setEmail] = useState(profile.email || "");
  const [isEmailVerified, setIsEmailVerified] = useState(!!profile.email);

  // Step 2.2: State Management for new settings
  const [crossfadeValue, setCrossfadeValue] = useState(0); // Default in seconds
  const [normalizationEnabled, setNormalizationEnabled] = useState(false);


  // Step 2.3: Load Settings
  useEffect(() => {
    const loadAppSettings = async () => {
      const loadedSettings = await dbHelpers.getSettings();
      if (loadedSettings) {
        setCrossfadeValue(loadedSettings.crossfade ?? 0);
        setNormalizationEnabled(loadedSettings.normalization ?? false);
        // Note: Existing settings like equalizer, effects, audioVolume are managed by useAudioSettings
        // and also stored in the same 'user-settings' object in DB.
        // This component specifically manages crossfade and normalization for now.
      }
    };
    loadAppSettings();
  }, []);

  // Step 2.4: Save Settings - Normalization
  const handleNormalizationChange = useCallback(async (newCheckedState: boolean) => {
    setNormalizationEnabled(newCheckedState);
    try {
      const currentSettings = await dbHelpers.getSettings() || {};
      currentSettings.normalization = newCheckedState;
      await dbHelpers.updateSettings(currentSettings);
      toast({ description: "Normalization settings updated.", duration: 2000 });
    } catch (error) {
      console.error("Failed to save normalization settings:", error);
      toast({ description: "Failed to save settings.", variant: "destructive", duration: 2000 });
    }
  }, [toast]);

  // Step 2.4: Save Settings - Crossfade
  const handleCrossfadeChange = useCallback(async (newValue: number) => {
    setCrossfadeValue(newValue);
    try {
      const currentSettings = await dbHelpers.getSettings() || {};
      currentSettings.crossfade = newValue;
      await dbHelpers.updateSettings(currentSettings);
      toast({ description: "Crossfade settings updated.", duration: 2000 });
    } catch (error) {
      console.error("Failed to save crossfade settings:", error);
      toast({ description: "Failed to save settings.", variant: "destructive", duration: 2000 });
    }
  }, [toast]);


  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        updateProfile({
          avatar: reader.result as string,
          memberSince: profile.memberSince || new Date().toISOString(),
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateProfile({
      name: e.target.value,
      memberSince: profile.memberSince || new Date().toISOString(),
    });
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setIsEmailVerified(false);
  };

  const handleEmailVerify = () => {
    if (!email || !isValidEmail(email)) {
      alert("Please enter a valid email address.");
      return;
    }
    // This is a demo function. In a real app, you would implement actual email verification.
    updateProfile({
      email,
      memberSince: profile.memberSince || new Date().toISOString(),
    });
    setIsEmailVerified(true);
    toast({
      description: "Email verified successfully!",
      duration: 2000,
    });
  };

  const isValidEmail = (email: string) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(email);
  };

  const isValidDate = (dateString: string) => {
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime());
  };

  const formatMemberSince = (dateString?: string) => {
    if (!dateString || !isValidDate(dateString)) {
      return "Not set";
    }
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const calculateObjectSize = (obj: any): number => {
    if (obj === null || obj === undefined) return 0;
    if (obj instanceof Blob) return obj.size;
    if (typeof obj !== "object") {
      // Handle primitive types
      if (typeof obj === "string") return new TextEncoder().encode(obj).length;
      if (typeof obj === "number") return 8;
      if (typeof obj === "boolean") return 4;
      return 0;
    }

    // Handle arrays and objects recursively
    return Object.keys(obj).reduce((total, key) => {
      return total + calculateObjectSize(obj[key]);
    }, 0);
  };

  const calculateAppSize = async () => {
    setIsCalculating(true);
    try {
      // Calculate localStorage size
      let localStorageSize = 0;
      for (const key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          const item = localStorage.getItem(key);
          if (item) {
            localStorageSize += new Blob([item]).size;
          }
        }
      }

      let indexedDBSize = 0;

      if ("storage" in navigator && "estimate" in navigator.storage) {
        const storageEstimate = await navigator.storage.estimate();

        // Access usageDetails using a type assertion to bypass TypeScript error
        const usageDetails = (storageEstimate as any).usageDetails;

        if (usageDetails && usageDetails.indexedDB) {
          indexedDBSize = usageDetails.indexedDB;
        } else if (storageEstimate.usage !== undefined) {
          // Approximate IndexedDB size if usageDetails is not available
          indexedDBSize = storageEstimate.usage - localStorageSize;
        }

        const totalSize = localStorageSize + indexedDBSize;

        setAppSize({
          total: totalSize,
          localStorage: localStorageSize,
          indexedDB: indexedDBSize,
        });
      } else {
        console.error("StorageManager API is not supported in this browser.");
        const totalSize = localStorageSize; // IndexedDB size is unknown
        setAppSize({
          total: totalSize,
          localStorage: localStorageSize,
          indexedDB: 0,
        });
      }
    } catch (error) {
      console.error("Error calculating app size:", error);
    } finally {
      setIsCalculating(false);
    }
  };

  useEffect(() => {
    calculateAppSize();
  }, []);

  const resetAppData = async () => {
    try {
      // Clear all browser storage mechanisms
      localStorage.clear();
      sessionStorage.clear();

      // Clear IndexedDB
      await new Promise((resolve, reject) => {
        const request = indexedDB.deleteDatabase(DB_NAME);
        request.onsuccess = () => resolve(true);
        request.onerror = () => reject(request.error);
        request.onblocked = () => {
          console.warn("Database deletion was blocked");
          resolve(false);
        };
      });

      // Clear Service Worker cache
      if ("serviceWorker" in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
        }
      }

      // Clear browser cache (if possible)
      if ("caches" in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
      }

      // Reset profile state
      resetProfile();

      // Force complete page reload to ensure clean state
      window.location.reload();
    } catch (error) {
      console.error("Error resetting app data:", error);
      toast({
        description: "Failed to reset app data. Please try again or contact support.",
        duration: 2000,
      });
    }
  };

  const handleDeveloperModeClick = () => {
    // If already enabled, disable it
    if (profile.developerMode) {
      updateProfile({ developerMode: false });
      toast({
        description: "Developer mode disabled",
        duration: 2000,
      });
      return;
    }

    // If they were a dev before, enable immediately
    if (profile.wasDevBefore) {
      updateProfile({ developerMode: true });
      toast({
        description: "Developer mode enabled",
        duration: 2000,
      });
      return;
    }

    // First time enabling - need 7 taps
    const newTaps = profile.developerModeTaps + 1;
    const tapsNeeded = 7;
    const remainingTaps = tapsNeeded - newTaps;

    updateProfile({ developerModeTaps: newTaps });

    if (remainingTaps > 0) {
      toast({
        description: `${remainingTaps} more ${remainingTaps === 1 ? 'tap' : 'taps'} to enable developer mode`,
        duration: 2000,
      });
    } else if (newTaps === tapsNeeded) {
      updateProfile({ 
        developerMode: true, 
        developerModeTaps: 0,
        wasDevBefore: true 
      });
      toast({
        description: "You are now a developer!",
        duration: 2000,
      });
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between gap-3 mb-8">
        <h1 className="text-4xl font-bold mb-2">Settings</h1>
        <VersionDisplay />
      </div>

      <div className="space-y-8">
        <SettingsSection icon={<User className="w-6 h-6 text-yellow-500" />} title="Profile">
          <div className="flex flex-col md:flex-row gap-8 items-start">
            <div className="relative group min-w-40 w-40 h-40 rounded-full overflow-hidden shadow-xl">
              {profile.avatar ? (
                <img src={profile.avatar} alt={profile.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-zinc-700 flex items-center justify-center">
                  <User className="w-20 h-20 text-zinc-500" />
                </div>
              )}
              <label className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity duration-300">
                <Camera className="w-8 h-8 text-white mb-2" />
                <span className="text-white text-sm">Change Photo</span>
                <input type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
              </label>
            </div>

            <div className="flex flex-wrap w-full gap-6">
              <div className="w-full lg:w-[calc(50%-1.5rem)]">
                <label htmlFor="name" className="block text-sm font-medium mb-2 text-zinc-400">
                  Display Name
                </label>
                <input
                  id="name"
                  type="text"
                  value={profile.name}
                  onChange={handleNameChange}
                  className="w-full px-4 py-2 bg-zinc-700 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 transition-all duration-300"
                  placeholder="Your name"
                />
              </div>
              <div className="flex flex-col gap-4 w-full lg:w-[calc(50%-1.5rem)]">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-2 text-zinc-400">
                    Email
                  </label>
                  <div className="flex gap-2 w-full">
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={handleEmailChange}
                      className="flex-1 min-w-0 px-4 py-2 bg-zinc-700 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 transition-all duration-300"
                      placeholder="Your email"
                    />
                    <button
                      onClick={handleEmailVerify}
                      className={`min-w-[90px] px-4 py-2 rounded-md transition-all duration-300 flex items-center justify-center gap-2 ${
                        isEmailVerified
                          ? "bg-green-500 text-white cursor-default"
                          : "bg-yellow-500 text-black hover:bg-yellow-600"
                      }`}
                      disabled={isEmailVerified}
                    >
                      {isEmailVerified && <Check className="w-4 h-4" />}
                      <span>{isEmailVerified ? "Verified" : "Verify"}</span>
                    </button>
                  </div>
                </div>

                <div className="mt-2">
                  <p className="text-sm text-zinc-400">Member Since</p>
                  <p className="font-medium mt-1">{formatMemberSince(profile.memberSince)}</p>
                </div>
              </div>
            </div>
          </div>
        </SettingsSection>

        <SettingsSection icon={<Shield className="w-6 h-6 text-red-500" />} title="Privacy">
          <div className="space-y-4">
            <ToggleSwitch
              checked={profile.showListeningStatus}
              onChange={(e) => updateProfile({ showListeningStatus: e.target.checked })}
              label="Show Listening Status"
              description="Let others see what you're playing"
            />
            <ToggleSwitch
              checked={profile.shareProfile}
              onChange={(e) => updateProfile({ shareProfile: e.target.checked })}
              label="Share Profile"
              description="Make your profile visible to others"
            />
          </div>
        </SettingsSection>

        <SettingsSection icon={<Bell className="w-6 h-6 text-yellow-500" />} title="Notifications">
          <div className="space-y-4">
            <ToggleSwitch
              checked={profile.emailNotifications}
              onChange={(e) => updateProfile({ emailNotifications: e.target.checked })}
              label="Email Notifications"
              description="Receive updates and newsletters"
            />
            <ToggleSwitch
              checked={profile.pushNotifications}
              onChange={(e) => updateProfile({ pushNotifications: e.target.checked })}
              label="Push Notifications"
              description="Get real-time updates about your music"
            />
          </div>
        </SettingsSection>

        <SettingsSection icon={<Cloud className="w-6 h-6 text-[#1DB954]" />} title="Spotify Integration">
          <div className="space-y-4">
            {isAuthenticated ? (
              <button
                onClick={logout}
                className="px-4 py-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition"
              >
                Disconnect Spotify
              </button>
            ) : (
              <button
                onClick={login}
                className="px-4 py-2 bg-[#1DB954] text-white rounded-full hover:bg-[#1ed760] transition"
              >
                Connect Spotify
              </button>
            )}
          </div>
        </SettingsSection>

        <SettingsSection icon={<Database className="w-6 h-6 text-orange-500" />} title="App Management">
          <div className="space-y-6">
            <div>
              <h3 className="font-medium mb-2">Storage Usage</h3>
              {isCalculating ? (
                <p>Calculating storage usage...</p>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold">{formatFileSize(appSize.total)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>LocalStorage:</span>
                    <span>{formatFileSize(appSize.localStorage)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>IndexedDB:</span>
                    <span>{formatFileSize(appSize.indexedDB)}</span>
                  </div>
                  <button
                    onClick={calculateAppSize}
                    className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors duration-300"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Refresh
                  </button>
                </div>
              )}
            </div>

            <div className="pt-4 border-t border-zinc-700">
              <button
                onClick={() => setShowResetConfirm(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition"
              >
                <Trash2 className="w-4 h-4" />
                Reset App Data
              </button>
              <p className="mt-2 text-sm text-zinc-400">
                This will delete all your local data and reset the app to its initial state
              </p>
            </div>
          </div>
        </SettingsSection>

        {/* Step 2.5: UI Implementation - Playback Settings Section */}
        <SettingsSection icon={<PlayCircle className="w-6 h-6 text-green-500" />} title="Playback">
          <div className="space-y-6">
            <ToggleSwitch
              checked={normalizationEnabled}
              onChange={(e) => handleNormalizationChange(e.target.checked)}
              label="Audio Normalization"
              description="Adjust all tracks to a similar loudness level"
            />
            <div>
              <label htmlFor="crossfade" className="block text-sm font-medium mb-2 text-zinc-300">
                Crossfade Duration: <span className="font-bold text-yellow-500">{crossfadeValue}s</span>
              </label>
              <p className="text-sm text-zinc-400 mb-3">
                Smoothly transition between tracks (0 for no crossfade).
              </p>
              <input
                id="crossfade"
                type="range"
                min="0"
                max="10" // Max 10 seconds for crossfade
                step="1"
                value={crossfadeValue}
                onChange={(e) => handleCrossfadeChange(parseInt(e.target.value, 10))}
                className="w-full h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-yellow-500"
              />
            </div>
          </div>
        </SettingsSection>


        <SettingsSection icon={<Zap className="w-6 h-6 text-purple-500" />} title="Advanced">
          <div className="space-y-4">
            <div className="cursor-pointer">
              <ToggleSwitch
                checked={profile.developerMode}
                onChange={() => handleDeveloperModeClick()}
                label="Developer Mode"
                description={profile.wasDevBefore ? 
                  "Toggle developer features on/off" : 
                  "Tap multiple times to enable developer mode"}
              />
            </div>
          </div>
        </SettingsSection>

        <ConfirmDialog
          isOpen={showResetConfirm}
          onClose={() => setShowResetConfirm(false)}
          onConfirm={resetAppData}
          title="Reset App Data"
          message="Are you sure you want to reset all app data? This action cannot be undone."
          confirmLabel="Reset"
          type="danger"
        />
      </div>
    </div>
  );
};
